import axios from 'axios';
import { MembersApi } from './services/membersApi';
import { chunk } from 'lodash';
import { GroupAppsApi } from './services';
var MAX_INVITE_EMAILS_IN_ONE_REQUEST = 20;
/**
 * Browser client for [Wix Group Api](https://bo.wix.com/wix-docs/rest/business-apis#social-groups)
 */
var Api = /** @class */ (function () {
    /**
     * @example
     * ```typescript
     * import getBaseUrl from '@wix/social-groups-common/dist/src/baseUrl';
     * api = new Api(appParams.instance, getBaseUrl(wixCodeApi));
     * ```
     * @param {string} token - An instance is a signed token created by the clientSpecMap library (owned by MetaSite team).
     * (see [more](https://github.com/wix-private/platformization-guidelines/blob/master/guides/instance.md#instance))
     * @param {string} baseURL
     */
    function Api(token, baseURL, context) {
        this.axios = axios.create({
            baseURL: baseURL,
            headers: {
                Authorization: token,
            },
        });
        this.members = new MembersApi(token, baseURL);
        this.groupAppsApi = new GroupAppsApi(context);
    }
    Api.prototype.getInstance = function () {
        return this.axios;
    };
    Api.prototype.getGroup = function (id) {
        return this.axios.get("/v1/groups/" + id);
    };
    /**
     * @deprecated
     * use members namespace
     * @example
     * ```
     * const api = new Api(token, url);
     * api.members.listGroupMembers(id)
     * ```
     * @param {string} id
     * @return {Promise<AxiosResponse<T>>}
     */
    Api.prototype.getGroupMembers = function (id) {
        return this.members.listGroupMembers(id);
    };
    Api.prototype.getGroupList = function () {
        return this.axios.get("/v1/groups");
    };
    Api.prototype.listGroups = function (request) {
        // https://bo.wix.com/wix-docs/rest/all-apis#social-groups.social-groups.list-groups-1
        return this.axios.post("/v1/groups/query", request);
    };
    Api.prototype.createGroup = function (groupId, details, settings) {
        if (settings === void 0) { settings = {}; }
        return this.axios.post("/v1/groups", {
            groupId: groupId,
            details: details,
            settings: settings,
        });
    };
    Api.prototype.joinPublicAndSecretGroup = function (groupId, membershipQuestionsAnswers) {
        return this.members.joinGroup(groupId, membershipQuestionsAnswers);
    };
    Api.prototype.requestToJoinPrivateGroup = function (groupId, membershipQuestionsAnswers) {
        return this.members.submitRequestToJoin(groupId, membershipQuestionsAnswers);
    };
    Api.prototype.withdrawRequestToJoinPrivateGroup = function (groupId) {
        return this.members.withdrawRequestToJoin(groupId);
    };
    Api.prototype.leaveGroup = function (groupId) {
        return this.members.leaveGroup(groupId);
    };
    Api.prototype.getGroupMember = function (groupId, siteMemberId) {
        return this.members.getGroupMember(groupId, siteMemberId);
    };
    Api.prototype.setMemberRole = function (request) {
        return this.axios.post("v1/groups/" + request.groupId + "/members/" + request.siteMemberId + "/role", { role: request.role });
    };
    Api.prototype.removeMembersFromGroup = function (request) {
        return this.axios.post("v1/groups/" + request.groupId + "/members/remove", {
            siteMemberIds: request.siteMemberIds,
        });
    };
    Api.prototype.getMediaToken = function () {
        return this.axios.get('/v1/integration/media/token');
    };
    Api.prototype.patchGroup = function (groupId, body) {
        return this.axios.patch("v1/groups/" + groupId, body);
    };
    Api.prototype.getGroupInstanceSettings = function () {
        return this.axios.get('v1/groups/settings');
    };
    Api.prototype.updateGroupInstanceSettings = function (body) {
        return this.axios.patch('v1/groups/settings', body);
    };
    Api.prototype.deleteGroup = function (groupId) {
        return this.axios.delete("v1/groups/" + groupId);
    };
    Api.prototype.getInviteLink = function (groupId) {
        return this.axios.get("v1/groups/" + groupId + "/invites/email");
    };
    Api.prototype.inviteMembersByEmail = function (_a) {
        var _this = this;
        var groupId = _a.groupId, emails = _a.emails;
        var requestsList = chunk(emails, MAX_INVITE_EMAILS_IN_ONE_REQUEST).map(function (emailsChunk) {
            return _this.axios.post("v1/groups/" + groupId + "/invites/email-batch", {
                emails: emailsChunk,
            });
        });
        return Promise.allSettled(requestsList);
    };
    Api.prototype.getDefaultGroup = function () {
        return this.axios.get("/v1/internal/groups/default");
    };
    Api.prototype.getDefaultPostContent = function () {
        return this.axios.get("/v1/internal/groups/default/post");
    };
    Api.prototype.getRules = function (groupId) {
        return this.axios.get("v1/groups/" + groupId + "/rules");
    };
    Api.prototype.getRuleTemplates = function () {
        return this.axios.get("v1/groups/default/rules");
    };
    Api.prototype.updateRules = function (groupId, rules) {
        return this.axios.post("v1/groups/" + groupId + "/rules", { rules: rules });
    };
    Api.prototype.updateClientData = function (groupId, key, value) {
        return this.axios.patch("v1/groups/" + groupId + "/client_data", { key: key, value: value });
    };
    return Api;
}());
export default Api;
//# sourceMappingURL=api.js.map